import React, { useEffect, useRef, useState } from "react";
import Select, { SingleValue } from "react-select";
import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./PaymentSection.module.css";
import { IServiceOption, TUpdateQuestionnaireScoreTotalAmountRequest } from "../../services/types/payment.types";
import {
  useGetActiveQuestionnaireScoreStatusesQuery,
  useGetTherapistServicesWithPricesQuery,
} from "../../services/api/payment.api";
import { useAppSelector } from "../../services/hooks";
import { useActions } from "../../services/hooks/useActions";
import { roundPrice } from "../../utils/constants";
import { paymentSessionSelector, questionnaireScoreStatusOptionsSelector } from "../../services/selectors/paymentSession.selectors";

interface PaymentSectionProps {
  onSubmit?: (paymentInfo: TUpdateQuestionnaireScoreTotalAmountRequest) => Promise<void>;
  /** Pre-fill from GetQuestionnaireAnswersForEdit (e.g. when opening Action Report from Therapists Questions Reports) */
  initialTotalAmount?: number | null;
  initialCompanyServiceId?: number | null;
  initialQuestionnaireScoreStatusId?: number | null;
  note?: string;
  patientGuid?: string | null;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({
  onSubmit,
  initialTotalAmount,
  initialCompanyServiceId,
  initialQuestionnaireScoreStatusId,
  note,
  patientGuid,
}) => {
  const paymentSession = useAppSelector(paymentSessionSelector);
  const questionnaireScoreStatusOptions = useAppSelector(questionnaireScoreStatusOptionsSelector);
  const { serviceOptions, sessionPrice, selectedServiceId, selectedQuestionnaireScoreStatusId } = paymentSession;

  useGetTherapistServicesWithPricesQuery(patientGuid ?? undefined, { refetchOnMountOrArgChange: true });
  useGetActiveQuestionnaireScoreStatusesQuery();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const initialAppliedRef = useRef(false);

  const { setPaymentService, setSessionPrice, setSelectedQuestionnaireScoreStatus } = useActions();

  // Apply initial values from GetQuestionnaireAnswersForEdit (e.g. when editing from Therapists Questions Reports)
  useEffect(() => {
    if (initialAppliedRef.current) return;
    const hasInitial =
      (initialTotalAmount !== undefined && initialTotalAmount !== null) ||
      (initialCompanyServiceId !== undefined && initialCompanyServiceId !== null) ||
      (initialQuestionnaireScoreStatusId !== undefined && initialQuestionnaireScoreStatusId !== null);
    if (!hasInitial) return;

    if (initialTotalAmount !== undefined && initialTotalAmount !== null) {
      setSessionPrice(roundPrice(initialTotalAmount));
    }
    if (initialQuestionnaireScoreStatusId !== undefined && initialQuestionnaireScoreStatusId !== null) {
      setSelectedQuestionnaireScoreStatus(initialQuestionnaireScoreStatusId);
    }
    if (initialCompanyServiceId !== undefined && initialCompanyServiceId !== null) {
      // Defer until service options are loaded — don't mark applied yet so we retry when they arrive
      if (!serviceOptions?.length) return;
      const serviceOption = serviceOptions.find(
        (opt) => opt.service?.serviceId === initialCompanyServiceId
      );
      if (serviceOption) {
        setPaymentService({
          serviceId: initialCompanyServiceId,
          serviceName: serviceOption.service.serviceName,
          price: initialTotalAmount ?? serviceOption.service.priceAfterTax ?? 0,
        });
      }
    }
    initialAppliedRef.current = true;
  }, [
    initialTotalAmount,
    initialCompanyServiceId,
    initialQuestionnaireScoreStatusId,
    serviceOptions,
    setPaymentService,
    setSessionPrice,
    setSelectedQuestionnaireScoreStatus,
  ]);

  // Auto-select patient's default service when nothing is pre-filled
  useEffect(() => {
    if (selectedServiceId || !serviceOptions.length) return;
    // If an explicit initial service is expected, let the initial-values effect handle it
    if (initialCompanyServiceId !== undefined && initialCompanyServiceId !== null) return;
    const defaultService = serviceOptions.find((opt) => opt.service.isDefault);
    if (defaultService) {
      setPaymentService({
        serviceId: defaultService.service.serviceId,
        serviceName: defaultService.service.serviceName,
        price: defaultService.service.priceAfterTax,
      });
    }
  }, [serviceOptions, selectedServiceId, initialCompanyServiceId, setPaymentService]);

  const handleServiceSelectChange = (selected: SingleValue<IServiceOption>) => {
    if (selected && serviceOptions) {
      if (selected) {
        setPaymentService({
          serviceId: selected.value,
          serviceName: selected.service.serviceName,
          price: selected.service.priceAfterTax,
        });
      }
    }
  };

  const handleQuestionnaireScoreStatusChange = (selected: SingleValue<{ value: number; label: string }>) => {
    if (selected) {
      setSelectedQuestionnaireScoreStatus(selected.value);
    }
  };

  const isValid = selectedServiceId && selectedQuestionnaireScoreStatusId;
  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitSuccess(false);

    setIsSubmitting(true);

    try {
      const paymentInfo: TUpdateQuestionnaireScoreTotalAmountRequest = {
        totalAmount: sessionPrice,
        companyServiceId: selectedServiceId || 0,
        questionnaireCompaniesScoreUsersStatusesId: selectedQuestionnaireScoreStatusId,
      };

      // Call parent's onSubmit handler with payment info
      await onSubmit?.(paymentInfo);

      setSubmitSuccess(true);
    } catch (error) {
      console.error("Failed to submit appointment:", error);
      setSubmitError(t("ACTION_REPORT_PAYMENT_SUBMIT_ERROR") || "Failed to save appointment");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.paymentSection}>
      <h4 className={styles.sectionTitle}>
        {t("ACTION_REPORT_SESSION_DETAILS") || "Session Details"}
      </h4>
      <p className={styles.note}>{note}</p>
      <div className={styles.paymentFields}>
        <div className={styles.paymentField}>
          <label className={styles.paymentLabel}>
            {t("ACTION_REPORT_SERVICE_SELECT") || "Service"} *
          </label>
          <Select
            options={serviceOptions}
            value={serviceOptions.find(opt => opt.value === selectedServiceId) || null}
            onChange={handleServiceSelectChange}
            placeholder={t("ACTION_REPORT_SERVICE_PLACEHOLDER") || "Select service..."}
            className={styles.serviceSelect}
            classNamePrefix="service"
            isDisabled={isSubmitting}
          />
        </div>
        <div className={styles.paymentField}>
          <label className={styles.paymentLabel}>
            {t("ACTION_REPORT_SESSION_PRICE_LABEL") || "Session Price"} *
          </label>
          <input
            type="number"
            value={sessionPrice}
            onChange={(e) => setSessionPrice(roundPrice(Number(e.target.value)))}
            step="0.01"
            className={styles.input}
            placeholder="0.00"
            disabled={isSubmitting}
          />
        </div>
        <div className={styles.paymentField}>
          <label className={styles.paymentLabel}>
            {t("ACTION_REPORT_QUESTIONNAIRE_SCORE_STATUS") || "Appointment Status"} *
          </label>
          <Select
            options={questionnaireScoreStatusOptions}
            value={questionnaireScoreStatusOptions.find(opt => opt.value === selectedQuestionnaireScoreStatusId) || null}
            onChange={handleQuestionnaireScoreStatusChange}
            placeholder={t("ACTION_REPORT_QUESTIONNAIRE_SCORE_STATUS_PLACEHOLDER") || "Select status..."}
            className={styles.serviceSelect}
            classNamePrefix="service"
            isDisabled={isSubmitting}
          />
        </div>
      </div>

      {submitError && (
        <div className={styles.errorMessage}>
          {submitError}
        </div>
      )}

      {submitSuccess && (
        <div className={styles.successMessage}>
          {t("ACTION_REPORT_APPOINTMENT_SAVED") || "Appointment saved successfully!"}
        </div>
      )}

      <p className={styles.note}>
        {t("ACTION_REPORT_PAYMENT_NOTE") || "לסיום הפגישה ללא שליחת דוח, מלא את פרטי הסיכום ולחץ 'שמור'."}
      </p>

      <button
        className={`button ${styles.submitBtn} ${!isValid || isSubmitting || !selectedServiceId || !selectedQuestionnaireScoreStatusId ? "button_disabled" : "hover"}`}
        onClick={handleSubmit}
        disabled={!isValid || isSubmitting || !selectedServiceId || !selectedQuestionnaireScoreStatusId}
      >
        {isSubmitting
          ? (t("SAVING") || "Saving...")
          : (t("SAVE_APPOINTMENT_SUBMIT_BUTTON") || "שמור פגישה")}
      </button>
    </div>
  );
};

export default PaymentSection;

